import { FC, PropsWithChildren, useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Tab, Tabs, Box } from "@mui/material";
import useBasePath from "src/utils/hooks/useBasePath";
import __ from "src/utils/Translations";
import {
  absencesAbsenceRequestsRead,
  absencesEmployeeAbsenceRecordsRead,
} from "src/utils/RoleBasedSecurity/permissions";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useFetchEmployee } from "src/Pages/Company/Absences/api/employee.queries";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import Title from "src/Pages/Company/Absences/Title/Title";
import EmployeeCardSkeleton from "src/Pages/Company/Absences/EmployeeCard/EmployeeCardSkeleton";
import EmployeeCard from "src/Pages/Company/Absences/EmployeeCard";
import AbsenceRecordsCalendar from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsCalendar";
import AbsenceRecordsEmployeeRequests from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsEmployeeRequests";
import AbsenceRecordsBalances from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsBalances";

interface TabPanelProps {
  index: number;
  value: number;
}

const TabPanel: FC<PropsWithChildren<TabPanelProps>> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`absence-records-tabpanel-${index}`}
    aria-labelledby={`absence-records-tab-${index}`}
    style={{
      marginTop: "1.5rem",
    }}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

function a11yProps(index: number) {
  return {
    id: `absence-records-tab-${index}`,
    "aria-controls": `absence-records-tabpanel-${index}`,
  };
}

const TAB_KEYS = {
  CALENDAR: "calendar",
  REQUESTS: "requests",
  BALANCES: "balances",
} as const;

const getTabIndexFromHash = (hash: string): number => {
  const tabKey = hash.replace("#", "");
  switch (tabKey) {
    case TAB_KEYS.CALENDAR:
      return 0;
    case TAB_KEYS.REQUESTS:
      return 1;
    case TAB_KEYS.BALANCES:
      return 2;
    default:
      return 0;
  }
};

const getHashFromTabIndex = (index: number): string => {
  switch (index) {
    case 0:
      return TAB_KEYS.CALENDAR;
    case 1:
      return TAB_KEYS.REQUESTS;
    case 2:
      return TAB_KEYS.BALANCES;
    default:
      return TAB_KEYS.CALENDAR;
  }
};

const AbsenceRecordsEmployee: FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const basePath = useBasePath();
  const history = useHistory();
  const canReadAbsenceRequests = usePermission(absencesAbsenceRequestsRead);
  const canReadAbsenceRecords = usePermission(
    absencesEmployeeAbsenceRecordsRead
  );
  const logStatus = useLogStatus();

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceRecordsEmployee.tsx",
    });
  }

  const [value, setValue] = useState(() =>
    getTabIndexFromHash(history.location.hash)
  );

  const { data: employee, isFetching: isFetchingEmployee } =
    useFetchEmployee(employeeId);

  useEffect(() => {
    const tabIndex = getTabIndexFromHash(history.location.hash);
    setValue(tabIndex);
  }, [history.location.hash]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    const hash = getHashFromTabIndex(newValue);
    history.replace({ hash });
  };

  return (
    <>
      <BreadcrumbsMui
        items={[
          {
            label: __("Nieobecności"),
            to: `${basePath}/absences`,
          },
          {
            label: __("Kartoteki Nieobecności Pracowników"),
            to: `${basePath}/absences/absence-records`,
          },
          {
            label: __("Nieobecności pracownika"),
          },
        ]}
      />
      <Title title={__("Nieobecności pracownika")} />
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label={__("Nawigacja nieobecności pracownika")}
        sx={{
          marginBottom: "1rem",
        }}
      >
        <Tab
          label={__("Kalendarz roczny")}
          disabled={!canReadAbsenceRecords}
          data-testid="absence-records-tab-calendar"
          {...a11yProps(0)}
        />
        <Tab
          label={__("Lista wniosków")}
          data-testid="absence-records-tab-absence-requests"
          disabled={!canReadAbsenceRequests}
          {...a11yProps(1)}
        />
        <Tab
          label={__("Wymiary nieobecności i obecności")}
          data-testid="absence-records-tab-balances"
          {...a11yProps(2)}
        />
      </Tabs>
      {isFetchingEmployee ? (
        <EmployeeCardSkeleton />
      ) : (
        <EmployeeCard data={employee} />
      )}

      <TabPanel value={value} index={0}>
        <AbsenceRecordsCalendar />
      </TabPanel>

      <TabPanel value={value} index={1}>
        <AbsenceRecordsEmployeeRequests />
      </TabPanel>

      <TabPanel value={value} index={2}>
        <AbsenceRecordsBalances />
      </TabPanel>
    </>
  );
};

export default AbsenceRecordsEmployee;
